import React, { useState, useRef, useEffect } from 'react';
import { 
  Bot, X, Send, Loader2, Sparkles, Settings2, Plus, Check, Copy,
  Columns, Trash2, RotateCcw, Zap, Save, History, Clock, ChevronRight,
  BookmarkPlus, Bookmark, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Popular models for comparison - curated list
const COMPARISON_MODELS = [
  { id: 'GPT-4o', name: 'GPT-4o', provider: 'OpenAI', color: 'bg-emerald-500', description: 'Most capable GPT' },
  { id: 'GPT-4o-Mini', name: 'GPT-4o Mini', provider: 'OpenAI', color: 'bg-emerald-400', description: 'Fast & affordable' },
  { id: 'Claude-3.5-Sonnet', name: 'Claude 3.5 Sonnet', provider: 'Anthropic', color: 'bg-orange-500', description: 'Balanced Claude' },
  { id: 'Claude-3-Opus', name: 'Claude 3 Opus', provider: 'Anthropic', color: 'bg-orange-600', description: 'Most capable Claude' },
  { id: 'Gemini-2.0-Flash', name: 'Gemini 2.0 Flash', provider: 'Google', color: 'bg-blue-500', description: 'Latest Gemini' },
  { id: 'Gemini-1.5-Pro', name: 'Gemini 1.5 Pro', provider: 'Google', color: 'bg-blue-600', description: 'Pro Gemini' },
  { id: 'Llama-3.1-405B', name: 'Llama 3.1 405B', provider: 'Meta', color: 'bg-purple-500', description: 'Largest Llama' },
  { id: 'Llama-3.1-70B', name: 'Llama 3.1 70B', provider: 'Meta', color: 'bg-purple-400', description: 'Fast Llama' },
  { id: 'Grok-2', name: 'Grok 2', provider: 'xAI', color: 'bg-red-500', description: 'xAI model' },
  { id: 'Mistral-Large', name: 'Mistral Large', provider: 'Mistral', color: 'bg-cyan-500', description: 'Mistral flagship' },
  { id: 'o1', name: 'o1', provider: 'OpenAI', color: 'bg-emerald-600', description: 'Reasoning model' },
  { id: 'o1-mini', name: 'o1 Mini', provider: 'OpenAI', color: 'bg-emerald-300', description: 'Fast reasoning' },
];

// Quick comparison prompts
const COMPARISON_PROMPTS = [
  { label: "Strategy comparison", prompt: "What's the best options strategy for a stock I'm moderately bullish on with 30 days to earnings?" },
  { label: "Risk assessment", prompt: "Analyze the risk/reward of selling a put spread on SPY 2% below current price, 45 DTE" },
  { label: "Market outlook", prompt: "Given current market conditions, should I focus on credit or debit spreads? Explain your reasoning." },
  { label: "Greeks impact", prompt: "How do I manage a position when IV crush is expected after earnings?" },
];

const PoeModelCompare = ({ isOpen, onClose }) => {
  const [selectedModels, setSelectedModels] = useState(['GPT-4o', 'Claude-3.5-Sonnet']);
  const [inputValue, setInputValue] = useState('');
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [responses, setResponses] = useState({});
  const [isComparing, setIsComparing] = useState(false);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [viewingSaved, setViewingSaved] = useState(null);
  
  const inputRef = useRef(null);
  const abortControllersRef = useRef({});

  // Fetch saved comparisons
  const fetchSavedComparisons = async () => {
    setLoadingSaved(true);
    try {
      const response = await fetch(`${API}/poe/comparisons/list?limit=50`);
      const data = await response.json();
      if (data.success) {
        setSavedComparisons(data.comparisons);
      }
    } catch (error) {
      console.error('Error fetching saved comparisons:', error);
    } finally {
      setLoadingSaved(false);
    }
  };

  // Load saved comparisons when history panel opens
  useEffect(() => {
    if (showHistory && isOpen) {
      fetchSavedComparisons();
    }
  }, [showHistory, isOpen]);

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Toggle model selection
  const toggleModel = (modelId) => {
    setSelectedModels(prev => {
      if (prev.includes(modelId)) {
        if (prev.length <= 2) {
          toast.error('Select at least 2 models to compare');
          return prev;
        }
        return prev.filter(id => id !== modelId);
      } else {
        if (prev.length >= 4) {
          toast.error('Maximum 4 models for comparison');
          return prev;
        }
        return [...prev, modelId];
      }
    });
  };

  // Stream response from a single model
  const streamModelResponse = async (modelId, prompt) => {
    const abortController = new AbortController();
    abortControllersRef.current[modelId] = abortController;

    setResponses(prev => ({
      ...prev,
      [modelId]: { content: '', isLoading: true, isStreaming: true, error: null, timestamp: new Date() }
    }));

    try {
      const response = await fetch(`${API}/poe/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
          model: modelId,
          temperature: 0.7,
          max_tokens: 2048,
          stream: true,
          system_message: "You are an expert options trading advisor. Be concise but thorough. Focus on actionable insights."
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                fullContent += data.content;
                setResponses(prev => ({
                  ...prev,
                  [modelId]: { ...prev[modelId], content: fullContent }
                }));
              }
              if (data.done) {
                setResponses(prev => ({
                  ...prev,
                  [modelId]: { ...prev[modelId], isLoading: false, isStreaming: false }
                }));
              }
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (e) {
              if (e.message && !e.message.includes('JSON')) {
                throw e;
              }
            }
          }
        }
      }

      setResponses(prev => ({
        ...prev,
        [modelId]: { ...prev[modelId], isLoading: false, isStreaming: false }
      }));

    } catch (error) {
      if (error.name === 'AbortError') {
        setResponses(prev => ({
          ...prev,
          [modelId]: { 
            ...prev[modelId], 
            content: prev[modelId]?.content + '\n\n*[Stopped]*',
            isLoading: false, 
            isStreaming: false 
          }
        }));
      } else {
        console.error(`Error from ${modelId}:`, error);
        setResponses(prev => ({
          ...prev,
          [modelId]: { 
            content: '', 
            isLoading: false, 
            isStreaming: false, 
            error: error.message 
          }
        }));
      }
    }
  };

  // Run comparison
  const runComparison = async (prompt = inputValue) => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    if (selectedModels.length < 2) {
      toast.error('Select at least 2 models to compare');
      return;
    }

    setIsComparing(true);
    setResponses({});
    setCurrentPrompt(prompt.trim());
    setInputValue('');
    setViewingSaved(null);

    const promises = selectedModels.map(modelId => 
      streamModelResponse(modelId, prompt.trim())
    );

    await Promise.allSettled(promises);
    setIsComparing(false);
  };

  // Save current comparison
  const saveComparison = async () => {
    if (!currentPrompt || Object.keys(responses).length === 0) {
      toast.error('Nothing to save');
      return;
    }

    // Check if any response is still loading
    const anyLoading = Object.values(responses).some(r => r.isLoading || r.isStreaming);
    if (anyLoading) {
      toast.error('Wait for all responses to complete');
      return;
    }

    setIsSaving(true);
    try {
      const responsesData = {};
      for (const [modelId, response] of Object.entries(responses)) {
        if (response.content && !response.error) {
          const model = getModelInfo(modelId);
          responsesData[modelId] = {
            model_id: modelId,
            model_name: model.name,
            content: response.content,
            word_count: response.content.split(/\s+/).length,
            char_count: response.content.length
          };
        }
      }

      const response = await fetch(`${API}/poe/comparisons/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: currentPrompt,
          models: selectedModels,
          responses: responsesData,
          title: currentPrompt.length > 50 ? currentPrompt.substring(0, 50) + '...' : currentPrompt
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Comparison saved!');
        fetchSavedComparisons();
      } else {
        throw new Error(data.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving comparison:', error);
      toast.error('Failed to save comparison');
    } finally {
      setIsSaving(false);
    }
  };

  // Load a saved comparison
  const loadSavedComparison = (saved) => {
    setViewingSaved(saved);
    setCurrentPrompt(saved.prompt);
    setSelectedModels(saved.models);
    
    // Convert saved responses to display format
    const loadedResponses = {};
    for (const [modelId, response] of Object.entries(saved.responses)) {
      loadedResponses[modelId] = {
        content: response.content,
        isLoading: false,
        isStreaming: false,
        error: null
      };
    }
    setResponses(loadedResponses);
    setShowHistory(false);
  };

  // Stop all streams
  const stopAllStreams = () => {
    Object.values(abortControllersRef.current).forEach(controller => {
      try { controller.abort(); } catch (e) {}
    });
    abortControllersRef.current = {};
  };

  // Clear results
  const clearResults = () => {
    stopAllStreams();
    setResponses({});
    setCurrentPrompt('');
    setIsComparing(false);
    setViewingSaved(null);
  };

  // Copy response
  const copyResponse = (content, modelName) => {
    navigator.clipboard.writeText(content);
    toast.success(`${modelName} response copied`);
  };

  // Get model info
  const getModelInfo = (modelId) => {
    return COMPARISON_MODELS.find(m => m.id === modelId) || { 
      id: modelId, 
      name: modelId, 
      provider: 'Unknown', 
      color: 'bg-gray-500' 
    };
  };

  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      runComparison();
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if current comparison can be saved
  const canSave = currentPrompt && 
    Object.keys(responses).length > 0 && 
    !Object.values(responses).some(r => r.isLoading || r.isStreaming) &&
    !viewingSaved;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] w-[1600px] h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                <Columns className="h-5 w-5 text-white" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold">AI Model Comparison</DialogTitle>
                <DialogDescription className="text-xs">
                  Compare responses from multiple AI models side-by-side
                </DialogDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Save Button */}
              {canSave && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={saveComparison}
                  disabled={isSaving}
                  className="gap-1 border-green-300 hover:bg-green-50 text-green-700"
                  data-testid="save-comparison-btn"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <BookmarkPlus className="h-4 w-4" />
                  )}
                  Save
                </Button>
              )}
              
              {/* History Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                className={cn("gap-1", showHistory && "bg-muted")}
                data-testid="history-btn"
              >
                <History className="h-4 w-4" />
                Saved ({savedComparisons.length})
              </Button>
              
              {/* Models Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowModelPicker(!showModelPicker)}
                className="gap-1"
              >
                <Plus className="h-4 w-4" />
                Models ({selectedModels.length})
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={clearResults}
                className="h-8 w-8"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Model Picker */}
          {showModelPicker && (
            <div className="mt-3 p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Select 2-4 models to compare:</p>
              <div className="grid grid-cols-4 gap-2">
                {COMPARISON_MODELS.map(model => (
                  <div
                    key={model.id}
                    onClick={() => toggleModel(model.id)}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all",
                      selectedModels.includes(model.id) 
                        ? "bg-primary/10 border-primary" 
                        : "bg-background hover:bg-muted border-border"
                    )}
                  >
                    <Checkbox checked={selectedModels.includes(model.id)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <div className={cn("w-2 h-2 rounded-full", model.color)} />
                        <span className="text-sm font-medium truncate">{model.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{model.provider}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected Models Pills */}
          <div className="flex flex-wrap gap-2 mt-3">
            {selectedModels.map(modelId => {
              const model = getModelInfo(modelId);
              return (
                <Badge 
                  key={modelId} 
                  variant="secondary"
                  className="gap-1 pr-1"
                >
                  <div className={cn("w-2 h-2 rounded-full", model.color)} />
                  {model.name}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 ml-1 hover:bg-destructive/20"
                    onClick={() => toggleModel(modelId)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              );
            })}
            {viewingSaved && (
              <Badge variant="outline" className="gap-1 bg-amber-50 border-amber-300 text-amber-700">
                <Bookmark className="h-3 w-3" />
                Viewing saved comparison
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* History Sidebar */}
          {showHistory && (
            <div className="w-80 border-r bg-muted/30 flex flex-col">
              <div className="p-3 border-b bg-background">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Bookmark className="h-4 w-4" />
                  Saved Comparisons
                </h3>
              </div>
              <ScrollArea className="flex-1">
                {loadingSaved ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : savedComparisons.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    <Bookmark className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No saved comparisons yet</p>
                    <p className="text-xs mt-1">Run a comparison and click Save</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-2">
                    {savedComparisons.map((saved, idx) => (
                      <div
                        key={idx}
                        onClick={() => loadSavedComparison(saved)}
                        className={cn(
                          "p-3 rounded-lg border bg-background cursor-pointer hover:border-primary transition-all",
                          viewingSaved?.created_at === saved.created_at && "border-primary bg-primary/5"
                        )}
                      >
                        <p className="text-sm font-medium line-clamp-2 mb-2">
                          {saved.prompt}
                        </p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {saved.models.map(modelId => {
                            const model = getModelInfo(modelId);
                            return (
                              <Badge key={modelId} variant="secondary" className="text-xs py-0">
                                <div className={cn("w-1.5 h-1.5 rounded-full mr-1", model.color)} />
                                {model.name}
                              </Badge>
                            );
                          })}
                        </div>
                        <div className="flex items-center text-xs text-muted-foreground">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDate(saved.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          )}

          {/* Comparison Results Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Quick Prompts */}
            {Object.keys(responses).length === 0 && !viewingSaved && (
              <div className="px-4 py-3 border-b flex-shrink-0 bg-muted/30">
                <p className="text-sm text-muted-foreground mb-2">Quick comparison prompts:</p>
                <div className="flex flex-wrap gap-2">
                  {COMPARISON_PROMPTS.map((item, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      size="sm"
                      onClick={() => runComparison(item.prompt)}
                      className="text-xs"
                      disabled={isComparing}
                    >
                      <Zap className="h-3 w-3 mr-1" />
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Current Prompt Display */}
            {currentPrompt && (
              <div className="px-4 py-2 border-b bg-primary/5">
                <p className="text-sm">
                  <span className="font-medium">Prompt:</span> {currentPrompt}
                </p>
              </div>
            )}

            {/* Side by Side Results */}
            <div className="flex-1 overflow-hidden">
              {Object.keys(responses).length > 0 ? (
                <div className="h-full flex">
                  {selectedModels.map(modelId => {
                    const model = getModelInfo(modelId);
                    const response = responses[modelId] || {};
                    
                    return (
                      <div 
                        key={modelId} 
                        className="flex-1 flex flex-col border-r last:border-r-0 min-w-0"
                      >
                        {/* Model Header */}
                        <div className={cn(
                          "px-3 py-2 border-b flex items-center justify-between",
                          "bg-gradient-to-r from-muted/50 to-transparent"
                        )}>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-3 h-3 rounded-full", model.color)} />
                            <span className="font-medium text-sm">{model.name}</span>
                            {response.isStreaming && (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            )}
                          </div>
                          {response.content && !response.isStreaming && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyResponse(response.content, model.name)}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        
                        {/* Response Content */}
                        <ScrollArea className="flex-1 p-3">
                          {response.error ? (
                            <div className="text-destructive text-sm p-2 bg-destructive/10 rounded">
                              Error: {response.error}
                            </div>
                          ) : response.isLoading && !response.content ? (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span>Generating...</span>
                            </div>
                          ) : (
                            <div className="prose prose-sm dark:prose-invert max-w-none">
                              <p className="whitespace-pre-wrap text-sm leading-relaxed">
                                {response.content || ''}
                              </p>
                            </div>
                          )}
                        </ScrollArea>
                        
                        {/* Response Stats */}
                        {response.content && !response.isStreaming && (
                          <div className="px-3 py-1 border-t bg-muted/30 text-xs text-muted-foreground">
                            {response.content.split(/\s+/).length} words • {response.content.length} chars
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <Columns className="h-16 w-16 mx-auto mb-4 opacity-20" />
                    <p className="text-lg font-medium">Ready to Compare</p>
                    <p className="text-sm mt-1">
                      Enter a prompt below to see how {selectedModels.length} AI models respond differently
                    </p>
                    {savedComparisons.length > 0 && (
                      <p className="text-xs mt-3">
                        Or click <span className="font-medium">Saved</span> to view previous comparisons
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Input Area */}
        <div className="px-4 py-3 border-t bg-background flex-shrink-0">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter a prompt to compare across all selected models..."
              disabled={isComparing}
              className="flex-1"
            />
            {isComparing ? (
              <Button onClick={stopAllStreams} variant="destructive" className="px-4">
                <X className="h-4 w-4 mr-1" />
                Stop All
              </Button>
            ) : (
              <Button
                onClick={() => runComparison()}
                disabled={!inputValue.trim() || selectedModels.length < 2}
                className="px-4 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
              >
                <Send className="h-4 w-4 mr-1" />
                Compare
              </Button>
            )}
          </div>
          <p className="text-xs text-center text-muted-foreground mt-2">
            Comparing {selectedModels.length} models simultaneously via Poe.com
            {canSave && <span className="ml-2">• <span className="text-green-600 font-medium">Ready to save</span></span>}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PoeModelCompare;
